"use client";

import ThreeDLoading from "@/components/ThreeDLoading";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ThreeDPaymentModal = ({ isOpen, onClose, children, iframeSrc }) => {
  const isMobile = useIsMobile();
  const modalRef = useRef(null);
  const backdropRef = useRef(null);
  const iframeRef = useRef(null);
  const loadCountRef = useRef(0);

  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const escapePressed = useKeyPressed("Escape");
  const t = useTranslationDetector();

  useLockBodyScroll(isOpen && shouldRender);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsLoaded(false);
      loadCountRef.current = 0;
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsLoaded(false);
        loadCountRef.current = 0;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === backdropRef.current && onClose) {
        // onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isAnimating && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isAnimating]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const backdropClasses = useMemo(() => {
    const baseClasses = isMobile
      ? "md:bg-white/90 bg-black/50 fixed inset-0 z-50 flex items-end justify-center"
      : "md:bg-white/90 bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4";

    const backdropTransition = isAnimating
      ? "transition-all duration-300 ease-out"
      : "transition-all duration-300 ease-in";

    return clsx(baseClasses, backdropTransition, isAnimating ? "" : "");
  }, [isMobile, isAnimating]);

  const modalClasses = useMemo(() => {
    const baseClasses = clsx(
      "relative overflow-hidden bg-[#f6f6f6]",
      "transform transition-all duration-300",
    );

    if (isMobile) {
      return clsx(
        baseClasses,
        "w-full rounded-t-[32px]",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    } else {
      return clsx(
        baseClasses,
        "shadow-strong w-full max-w-[430px] rounded-[32px]",
        isAnimating
          ? "translate-y-0 scale-100 opacity-100 ease-out"
          : "translate-y-4 scale-95 opacity-0 ease-in",
      );
    }
  }, [isMobile, isAnimating]);

  const contentClasses = useMemo(() => {
    return clsx(
      "relative rounded-[32px] p-2",
      isMobile ? "h-[720px]" : "h-[720px] max-h-[90vh]",
    );
  }, [isMobile]);

  if (!shouldRender || typeof window === "undefined") return null;

  const modalContent = (
    <div
      ref={backdropRef}
      className={backdropClasses}
      onClick={handleBackdropClick}
    >
      <div className="pointer-events-none absolute inset-0 hidden overflow-hidden md:block">
        <div
          className="float-slow absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-blue-300/40"
          style={{ filter: "blur(100px)" }}
        ></div>
        <div
          className="float-medium absolute top-1/2 left-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-400/35"
          style={{ filter: "blur(90px)" }}
        ></div>
        <div
          className="float-slower absolute top-16 -right-16 h-96 w-96 rounded-full bg-blue-300/38"
          style={{ filter: "blur(110px)" }}
        ></div>
      </div>

      <div
        ref={modalRef}
        className={modalClasses}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={contentClasses}>
          <div className="h-full w-full overflow-hidden rounded-t-[24px] border border-[#e8e8e8] bg-white md:rounded-[24px]">
            <button
              className="absolute top-4 right-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#f6f6f6] text-[24px]"
              onClick={onClose}
            >
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["x-mark-custom"]}
                className="text-foreground-muted text-lg"
              />
            </button>

            <div
              className={clsx(
                "h-full w-full",
                !iframeSrc || !isLoaded ? "block" : "hidden",
              )}
            >
              <ThreeDLoading
                isIframeEnabled={false}
                title={t("common.3dPayment")}
                description={t("common.3dPaymentDescription")}
              />
            </div>

            <iframe
              src={iframeSrc}
              className={clsx(
                "h-full w-full",
                iframeSrc && isLoaded ? "block" : "hidden",
              )}
              title="3D Payment"
              onLoad={handleLoad}
              ref={iframeRef}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ThreeDPaymentModal;
